import type { HeatmapCellView, HeatmapView, RunSummaryView } from '../../../apps/apis/commandCenter/types';
import type { HeatValue } from '../types';
import type { RunStatsMap } from './catalog';

/** The grid never shows more than a day of columns, however long a window the API was asked for. */
const MAX_COLUMNS = 24;

/**
 * Local hour for a fixed UTC offset such as '+08:00'.
 * The server buckets on an offset rather than a zone name, so this mirrors that arithmetic exactly;
 * an offset that cannot be parsed falls back to UTC instead of silently shifting the axis.
 */
export const localHour = (now: Date, timezone: string): number => {
  const match = /^([+-])(\d{2}):(\d{2})$/.exec(timezone);
  const offsetMinutes = match ? (match[1] === '-' ? -1 : 1) * (Number(match[2]) * 60 + Number(match[3])) : 0;
  const minutes = now.getUTCHours() * 60 + now.getUTCMinutes() + offsetMinutes;
  return ((Math.floor(minutes / 60) % 24) + 24) % 24;
};

/**
 * Turns live runs (e.g. n8n executions, which are never stored in cc_runs) into heatmap cells so they
 * light up the grid the same way the server-aggregated cells do. Runs older than the window are dropped
 * — the grid has only 24 hour columns, so a stale run would otherwise reappear in a same-hour column.
 * Native runs are already in the server cells, so only pass the live-only runs to avoid double counting.
 */
export const heatCellsFromRuns = (
  runs: RunSummaryView[],
  timezone: string,
  windowHours: number,
  now: Date = new Date(),
): HeatmapCellView[] => {
  const cutoff = now.getTime() - windowHours * 3_600_000;
  const cells = new Map<string, HeatmapCellView>();
  for (const run of runs) {
    if (run.agentId === null) {
      continue;
    }
    const at = new Date(run.createdAt);
    if (Number.isNaN(at.getTime()) || at.getTime() < cutoff) {
      continue;
    }
    const hour = localHour(at, timezone);
    const key = `${run.agentId} ${hour}`;
    const cell = cells.get(key) ?? { agentId: run.agentId, hour, runs: 0, failures: 0 };
    cell.runs += 1;
    if (run.status === 'failed') {
      cell.failures += 1;
    }
    cells.set(key, cell);
  }
  return [...cells.values()];
};

/** Hour columns of the grid, oldest first and ending at the current hour in the reporting timezone. */
export const heatmapHours = (timezone: string, windowHours: number, now: Date = new Date()): number[] => {
  const columns = Math.max(1, Math.min(windowHours, MAX_COLUMNS));
  const current = localHour(now, timezone);
  return Array.from({ length: columns }, (_, i) => (current - columns + 1 + i + MAX_COLUMNS) % MAX_COLUMNS);
};

/** Density bucket for one hour; any failure in the hour outranks the volume. */
const cellValue = (runs: number, failures: number): HeatValue => {
  if (failures > 0) {
    return 'f';
  }
  if (runs === 0) {
    return 0;
  }
  return runs > 10 ? 3 : runs > 2 ? 2 : 1;
};

export interface AgentHeat {
  cells: HeatValue[];
}

/** One 24-cell row per agent the heatmap reported; agents with no cells simply get no row. */
export const heatByAgent = (view: HeatmapView | undefined, hours: number[]): Record<string, AgentHeat> => {
  if (!view) {
    return {};
  }
  const column = new Map(hours.map((hour, index) => [hour, index]));
  return view.cells.reduce<Record<string, AgentHeat>>((acc, cell) => {
    const index = column.get(cell.hour);
    const row = acc[cell.agentId] ?? { cells: hours.map(() => 0 as HeatValue) };
    if (index !== undefined) {
      row.cells[index] = cellValue(cell.runs, cell.failures);
    }
    acc[cell.agentId] = row;
    return acc;
  }, {});
};

/** Window totals per agent — the only run data the board has for a catalog card. */
export const runStatsFromHeatmap = (view: HeatmapView | undefined): RunStatsMap => {
  if (!view) {
    return {};
  }
  return view.cells.reduce<RunStatsMap>((acc, cell) => {
    const current = acc[cell.agentId] ?? { runs: 0, failures: 0 };
    acc[cell.agentId] = { runs: current.runs + cell.runs, failures: current.failures + cell.failures };
    return acc;
  }, {});
};
